/**
 * Who looks like what. A person's photo is their app profile photo (chat
 * `avatar_url`, keyed by HRMS user id). The HR web photo (`profile_photo_url`)
 * is only a fallback for rows that carry it.
 */
const PHOTO_URL = /^(https?:\/\/|data:image\/)/i;

/** A drawable photo URL, or null for blank/relative/junk values. */
export function photoUrlOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return PHOTO_URL.test(trimmed) ? trimmed : null;
}

function idOrNull(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return null;
  return value.trim() || null;
}

function codeKey(value: unknown): string | null {
  return idOrNull(value)?.toUpperCase() ?? null;
}

export type DirectoryPerson = { avatar_url?: string | null; id?: string | null; user_id?: string | null };

export function buildPhotoDirectory(
  people: readonly DirectoryPerson[] | null | undefined,
  self?: { avatar_url?: string | null; id?: string | null } | null,
): ReadonlyMap<string, string> {
  const photos = new Map<string, string>();
  for (const person of people ?? []) {
    const userId = idOrNull(person.user_id ?? person.id);
    const photo = photoUrlOrNull(person.avatar_url);
    if (userId && photo) photos.set(userId, photo);
  }
  const selfId = idOrNull(self?.id);
  if (selfId) {
    // The colleague list excludes the caller; their own photo updates the
    // instant they change it on the Profile screen.
    const own = photoUrlOrNull(self?.avatar_url);
    if (own) photos.set(selfId, own);
    else photos.delete(selfId);
  }
  return photos;
}

export type EmployeeIdentity = { employeeCode: string | null; employeeId: string; photoUrl: string | null; userId: string | null };
export type EmployeeHints = { employeeCode?: string | null; employeeId?: string | null };
export type EmployeeIdentityIndex = { resolve: (hints: EmployeeHints) => EmployeeIdentity | null };

export const EMPTY_EMPLOYEE_INDEX: EmployeeIdentityIndex = { resolve: () => null };

/** HR rows that only know `Employee.id` (emptID) or an employee code → the person. */
export function buildEmployeeIdentityIndex(
  identities: readonly EmployeeIdentity[] | null | undefined,
): EmployeeIdentityIndex {
  if (!identities?.length) return EMPTY_EMPLOYEE_INDEX;
  const byId = new Map<string, EmployeeIdentity>();
  const byCode = new Map<string, EmployeeIdentity | null>();
  for (const identity of identities) {
    byId.set(identity.employeeId, identity);
    const code = codeKey(identity.employeeCode);
    if (!code) continue;
    // Employee codes are not unique. A code two people share resolves to
    // nobody: initials beat someone else's face.
    const existing = byCode.get(code);
    byCode.set(code, byCode.has(code) && existing?.employeeId !== identity.employeeId ? null : identity);
  }
  return {
    resolve({ employeeCode, employeeId }) {
      const id = idOrNull(employeeId);
      const found = id ? byId.get(id) : undefined;
      if (found) return found;
      // A supplied-but-unknown employee id falls through to the code so rows
      // whose "employeeId" is actually a code (e.g. Workflow) still resolve;
      // an ambiguous code is already null above, so no wrong face.
      const code = codeKey(employeeCode);
      return code ? byCode.get(code) ?? null : null;
    },
  };
}
